package com.speedsoftware.nibbler.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.speedsoftware.nibbler.client.NibblerApiClient
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.models.UserDto
import com.speedsoftware.nibbler.models.UserRole

sealed interface Screen {
    object Login : Screen
    data class AcceptInvite(val token: String) : Screen
    /** Password reset flow; [token] is pre-filled when arriving via an email link. */
    data class ResetPassword(val token: String? = null) : Screen
    object AdminDashboard : Screen
    object ProjectsList : Screen
    data class Settings(val section: SettingsSection = SettingsSection.PROFILE) : Screen
    data class ProjectDetail(val project: ProjectDto) : Screen
}

/** Pages of the global settings screen. */
enum class SettingsSection(val title: String, val description: String) {
    PROFILE("Profile", "How you appear to the rest of your team."),
    SECURITY("Security", "Two-factor authentication and the devices that are logged in."),
    APPEARANCE("Appearance", "Choose how Nibbler looks."),
    ACCOUNT("Account", "Your account details and what happens if you leave.")
}

enum class ProjectTab {
    BOARDS,
    DOCS,
    MEMBERS,
    SETTINGS
}

class AppState(
    val apiClient: NibblerApiClient = NibblerApiClient()
) {
    var currentUser by mutableStateOf<UserDto?>(null)
    var currentScreen by mutableStateOf<Screen>(
        com.speedsoftware.nibbler.getInitialResetToken()?.let { Screen.ResetPassword(it) }
            ?: com.speedsoftware.nibbler.getInitialUrlToken()?.let { Screen.AcceptInvite(it) }
            ?: Screen.Login
    )
    var selectedProjectTab by mutableStateOf(ProjectTab.BOARDS)
    /** The login remembered from an earlier launch, if the platform keeps one. */
    private val storedSession = com.speedsoftware.nibbler.loadStoredSession()

    var serverBaseUrl by mutableStateOf(storedSession?.serverUrl ?: com.speedsoftware.nibbler.getDefaultServerUrl())

    /** True while the app checks whether the browser still holds a valid session (web, cookie sessions). */
    var isRestoringSession by mutableStateOf(false)
        private set

    /** One-off message for the login screen, e.g. after the session ran out. */
    var loginNotice by mutableStateOf<String?>(null)

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    init {
        apiClient.baseUrl = serverBaseUrl
        apiClient.mobileClient = com.speedsoftware.nibbler.isMobileApp()
        apiClient.cookieSessions = com.speedsoftware.nibbler.isSameOriginServer(serverBaseUrl)
        if (!apiClient.cookieSessions && currentScreen is Screen.Login) apiClient.authToken = storedSession?.token
        isRestoringSession = (apiClient.cookieSessions || apiClient.authToken != null) && currentScreen is Screen.Login
        apiClient.onUnauthorized = {
            // The server no longer accepts our session: back to the login screen
            if (currentUser != null) {
                currentUser = null
                currentScreen = Screen.Login
                loginNotice = "Your session has ended. Please log in again."
                com.speedsoftware.nibbler.storeSession(null)
            } else if (isRestoringSession) {
                // The remembered login is no longer valid
                apiClient.authToken = null
                com.speedsoftware.nibbler.storeSession(null)
            }
        }
    }

    /** Picks up a session the browser still holds (cookie) or the login remembered on this device, so a restart does not log the user out. */
    suspend fun restoreSession() {
        if (!isRestoringSession) return
        apiClient.getMe().onSuccess { user ->
            currentUser = user
            if (currentScreen is Screen.Login) currentScreen = Screen.ProjectsList
        }
        isRestoringSession = false
    }

    val isAuthenticated: Boolean
        get() = currentUser != null

    val isSuperAdmin: Boolean
        get() = currentUser?.role == UserRole.SUPER_ADMIN

    /** Completes a login: signs the user in and remembers the session where the platform allows it. */
    fun onAuthenticated(user: UserDto) {
        currentUser = user
        val token = apiClient.authToken
        if (!apiClient.cookieSessions && !token.isNullOrEmpty()) {
            com.speedsoftware.nibbler.storeSession(com.speedsoftware.nibbler.StoredSession(token, serverBaseUrl))
        }
    }

    fun logout() {
        // End the session on the server too; the client clears its token once that request has gone out
        scope.launch { apiClient.logout() }
        currentUser = null
        currentScreen = Screen.Login
        com.speedsoftware.nibbler.storeSession(null)
    }

    fun navigateTo(screen: Screen) {
        currentScreen = screen
    }
}
